import { CommonModule } from '@angular/common';
import {
    DynamicDialogConfig,
    DynamicDialogModule,
    DynamicDialogRef,
} from 'primeng/dynamicdialog';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { MessagesModule } from 'primeng/messages';
import { Message, MessageService } from 'primeng/api';
import { InputTextareaModule } from 'primeng/inputtextarea';
import {
    Checkbox,
    CheckboxChangeEvent,
    CheckboxModule,
} from 'primeng/checkbox';
import { Component, ViewChild, Injectable } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ProgressBarModule } from 'primeng/progressbar';
import { VehiclesService } from 'src/app/demo/service/vehicles.service';
import { ApproveKYCDocumentPayload } from 'src/app/demo/models/approve-document';
import { DocumentEntities } from 'src/app/demo/constants/document-entities';
import { DocumentsService } from 'src/app/demo/service/documents.service';
import { NgxTranslateModule } from 'src/app/translate.module';
import { languages } from 'src/app/demo/constants/languages';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/demo/service/language.service';
import { showErrorToast } from 'src/app/demo/utilities/toast-utils';

@Component({
    selector: 'app-verify-customer-kyc-document',
    standalone: true,
    imports: [
        CommonModule,
        DynamicDialogModule,
        ProgressBarModule,
        ButtonModule,
        CheckboxModule,
        NgxTranslateModule,
        InputTextareaModule,
        FormsModule,
        ReactiveFormsModule,
        DialogModule,
        MessagesModule,
    ],
    templateUrl: './verify-customer-kyc-document.component.html',
    styleUrl: './verify-customer-kyc-document.component.scss',
    providers: [MessagesModule],
})
export class VerifyCustomerKycDocumentComponent {
    @ViewChild('verificationProceedCheckbox') proceedCheckbox!: Checkbox;

    blobUrl!: SafeResourceUrl;
    mimeType!: string;
    customerId!: string;
    documentId!: string;
    entityId!: string;
    descriptionForm!: FormGroup;

    languages: any[] = languages;
    selectedLanguage!: string;

    documentEntity!: DocumentEntities;

    verificationProceed: boolean = false;
    approvalLoading: boolean = false;
    rejectionLoading: boolean = false;

    constructor(
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig,
        private fb: FormBuilder,
        private translateService: TranslateService,
        private languageService: LanguageService,
        private vehicleService: VehiclesService,
        private documentService: DocumentsService,
        private messageService: MessageService,
        private router: Router
    ) {
        this.blobUrl = this.config.data.blobUrl;
        this.mimeType = this.config.data.mimeType;
        this.customerId = this.config.data.customerId;
        this.documentId = this.config.data.documentId;
        this.entityId = this.config.data.entityId;
        this.documentEntity = this.config.data.entity;
    }

    ngOnInit() {
        this.initializeForm();
        this.initializeLocale();
    }

    initializeForm() {
        this.descriptionForm = this.fb.group({
            verificationComment: [''],
        });
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translateService.use(language);
        this.languageService.setLanguage(language);
    }

    showSuccess(message: string) {
        this.translateService.get(message).subscribe((translatedMessage) => {
            this.messageService.add({
                severity: 'success',
                summary: translatedMessage,
            });
        });
    }

    showError(message: string) {
        this.translateService.get(message).subscribe((translatedMessage) => {
            this.messageService.add({
                severity: 'error',
                summary: translatedMessage,
            });
        });
    }

    closeDialog() {
        this.ref.close();
    }

    onCheckboxClicked(event: CheckboxChangeEvent) {
        if (event.checked) {
            this.verificationProceed = true;
        } else {
            this.verificationProceed = false;
        }
    }

    approveClicked() {
        this.approvalLoading = true;
        this.documentService
            .approveCustomerDocument(
                this.getApprovalPayload(),
                this.customerId,
                this.documentId,
                this.entityId,
                this.documentEntity
            )
            .subscribe({
                next: (value) => {
                    this.approvalLoading = false;
                    this.navigateToDetailPage();
                    this.ref.close();
                },
                error: (err) => {
                    this.approvalLoading = false;
                    showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService, this.translateService)
                },
            });
    }

    navigateToDetailPage() {
        switch (this.documentEntity) {
            case DocumentEntities.CUSTOMER:
                this.router.navigate([
                    '/home/customers/'.concat(this.customerId),
                ]);
                break;
            case DocumentEntities.DRIVER:
                this.router.navigate([
                    '/home/customers/'
                        .concat(this.customerId)
                        .concat('/drivers/'.concat(this.entityId)),
                ]);
                break;
            case DocumentEntities.VEHICLE:
                this.router.navigate([
                    '/home/customers/'
                        .concat(this.customerId)
                        .concat('/vehicles/'.concat(this.entityId)),
                ]);
                break;

            default:
                this.router.navigate([
                    '/home/customers/'.concat(this.customerId),
                ]);
                break;
        }
    }

    rejectClicked() {
        this.rejectionLoading = true;
        this.documentService
            .approveCustomerDocument(
                this.getRejectedPayload(),
                this.customerId,
                this.documentId,
                this.entityId,
                this.documentEntity
            )
            .subscribe({
                next: (value) => {
                    this.rejectionLoading = false;
                    this.ref.close();
                },
                error: (err) => {
                    this.rejectionLoading = false;
                    this.ref.close()
                    showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService, this.translateService)
                },
            });
    }

    getApprovalPayload(): ApproveKYCDocumentPayload {
        return this.descriptionForm.get('verificationComment')?.value === null
            ? {
                  verificationComment: null,
                  action: 'APPROVE',
              }
            : {
                  verificationComment: this.descriptionForm.get(
                      'verificationComment'
                  )?.value,
                  action: 'APPROVE',
              };
    }

    getRejectedPayload(): ApproveKYCDocumentPayload {
        return this.descriptionForm.get('verificationComment')?.value === null
            ? {
                  verificationComment: null,
                  action: 'REJECT',
              }
            : {
                  verificationComment: this.descriptionForm.get(
                      'verificationComment'
                  )?.value,
                  action: 'REJECT',
              };
    }
}
